import { ChangeDetectionStrategy, ChangeDetectorRef, Component, forwardRef, Input, OnDestroy } from '@angular/core';
import { MultiSelectOption } from '../multi-select/multi-select.component';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Observable, Subject, Subscription, takeUntil } from 'rxjs';

@Component({
  selector: 'osn-cxb-multi-select-tag-control',
  templateUrl: './multi-select-tag-control.component.html',
  styleUrls: ['./multi-select-tag-control.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MultiSelectTagControlComponent),
      multi: true,
    },
  ],
})
export class MultiSelectTagControlComponent implements ControlValueAccessor, OnDestroy {
  @Input() public options: MultiSelectOption[];
  @Input() public isLoading$: Observable<boolean>;
  @Input() public hintLabel: string;
  @Input() public filterPlaceholderLabel: string;
  @Input() public placeholder: string;
  @Input() public filterNoEntriesFoundLabel: string;

  public multiSelectTagControl: FormControl<string[]>;
  private readonly destroyed$ = new Subject<void>();
  private multiSelectTagControlValueChangesSubscription: Subscription;

  public propagateChange: Function = () => {};
  public propagateTouch: Function = () => {};

  constructor(private readonly changeDetectorRef: ChangeDetectorRef) {}

  public removeOption(id: string) {
    const updatedValue = this.multiSelectTagControl.value.filter((selectedOption) => selectedOption !== id);
    this.multiSelectTagControl.setValue(updatedValue);
  }

  public ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  public writeValue(ids: string[] | null): void {
    if (!!this.multiSelectTagControlValueChangesSubscription) {
      this.multiSelectTagControlValueChangesSubscription.unsubscribe();
    }

    this.multiSelectTagControl = new FormControl<string[]>(ids);
    this.multiSelectTagControlValueChangesSubscription = this.multiSelectTagControl.valueChanges
      .pipe(takeUntil(this.destroyed$))
      .subscribe((value) => {
        if (!!value) {
          this.propagateChange?.(value);
        }
      });
    this.changeDetectorRef.detectChanges();
  }

  public registerOnChange(onChange: Function): void {
    this.propagateChange = onChange;
  }

  public registerOnTouched(onTouched: Function): void {
    this.propagateTouch = onTouched;
  }

  public setDisabledState?(isDisabled: boolean): void {
    if (isDisabled) {
      this.multiSelectTagControl.disable();
    } else {
      this.multiSelectTagControl.enable();
    }
  }
}
